import React, { useEffect, useState, useRef } from 'react';
import { getAllDevelopmentData, postOverviewAiQuestion, generateAiSummary } from '../../api';
import { useProjectSelection } from '../../context/ProjectSelectionContext';
import SlidingAiPanel from '../../components/SlidingAiPanel';
import IconButton from '../../components/AIAnalysisButton';
import { formatMarkdown } from '../../utils/markdownFormatter';
import LoadingSpinner from '../../components/LoadingSpinner';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { format } from 'date-fns';
import MobileGlassSlider from '../../components/MobileGlassSlider';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// Project Selector Component
const ProjectSelector: React.FC<{
  projects: any[];
  selectedProjectId: number | null;
  onSelectProject: (projectId: number) => void;
  windowWidth: number;
}> = ({ projects, selectedProjectId, onSelectProject, windowWidth }) => {
  console.log('ProjectSelector - projects:', projects);
  console.log('ProjectSelector - selectedProjectId:', selectedProjectId);
  
  if (!projects || projects.length === 0) {
    console.log('ProjectSelector - No projects available, not rendering');
    return (
      <div style={{ marginBottom: '1.5rem', color: '#999' }}>
        No projects available
      </div>
    );
  }

  return (
    <div style={{ 
      marginBottom: '1.5rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      flexWrap: 'wrap'
    }}>
      <label htmlFor="project-select" style={{ fontWeight: 500 }}>Project:</label>
      <select
        id="project-select"
        value={selectedProjectId || ''}
        onChange={(e) => onSelectProject(Number(e.target.value))}
        style={{
          padding: '0.5rem',
          borderRadius: '0.25rem',
          border: '1px solid #e0e0e0',
          backgroundColor: '#fff',
          minWidth: windowWidth >= 768 ? '200px' : '150px',
          maxWidth: '100%',
          width: windowWidth < 768 ? '100%' : 'auto',
          fontSize: '0.9rem',
          boxSizing: 'border-box'
        }}
      >
        {projects.map((project) => (
          <option key={project.projectId} value={project.projectId}>
            {project.projectName}
          </option>
        ))}
      </select>
    </div>
  );
};





interface IterationData {
  IterationId: string;
  IterationName: string;
  SummaryId: number;
  StartDate: string; // Using string instead of Date for frontend compatibility
  EndDate: string; // Using string instead of Date for frontend compatibility
  TimeFrame: string;
  StoryPointsPlanned: number;
  StoryPointsCompleted: number;
  BugsCreated: number;
  BugsResolved: number;
  BugsActive: number;
  PlannedWorkItemsCount: number;
  CompletedWorkItemsCount: number;
  CompletionPercentage: number;
  BuildFailureRate: number;
  PullRequestsTotal: number;
  PullRequestsCompleted: number;
  AveragePRReviewTime: number;
  WorkItemsByStatus: string;
  WorkItemsByType: string;
  WorkItemsByPriority: string;
  SummaryDate: string; // Using string instead of Date for frontend compatibility
  AISummary?: string;
  BurndownData?: string;
  ItemsOverEstimate?: number;
  ItemsUnderEstimate?: number;
  TopOverEstimateItems?: string;
  ProjectId?: number;
  ProjectName?: string;
}

interface SprintInfo {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
}

interface SprintBurndownData {
  labels: string[];
  planned: number[];
  actual: number[];
  sprintInfo: SprintInfo;
}

interface TopOverEstimateItem {
  id: string;
  title: string;
  developerName: string;
  originalEstimate: number;
  actualDuration: number;
  overEstimateRatio: number;
}

// Main Iterations Component
const DevelopmentIterations: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [aiAnswer, setAiAnswer] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [projects, setProjects] = useState<any[]>([]);
  const { selectedProjectId, setSelectedProjectId } = useProjectSelection();

  const [displayData, setDisplayData] = useState<any>(null);
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [selectedIterationIndex, setSelectedIterationIndex] = useState(0);
  const [generatingAI, setGeneratingAI] = useState<Record<string, boolean>>({});
  const [iterationAISummaries, setIterationAISummaries] = useState<Record<string, string>>({});
  const [currentStreams, setCurrentStreams] = useState<Record<string, ReadableStreamDefaultReader | null>>({});
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<{ 
    isOpen: boolean; 
    title: string; 
    content: string; 
    type?: 'iteration'; 
    iterationId?: string; 
  }>({
    isOpen: false,
    title: '',
    content: '',
    type: undefined,
    iterationId: undefined
  });

    // Responsive: track mobile layout
    
    useEffect(() => {
      const handleResize = () => setIsMobile(window.innerWidth <= 768);
      handleResize();
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);


  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch dashboard data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await getAllDevelopmentData();
        console.log('Development Data Response:', response);
        
        const dashboardData = response;
        setData(dashboardData);
        
        // Set projects data
        if (response.projects && response.projects.length > 0) {
          setProjects(response.projects);
          
          // Set default selected project if not already set
          if (!selectedProjectId && response.projects.length > 0) {
            setSelectedProjectId(response.projects[0].projectId);
            setSelectedProject(response.projects[0]);
          }
          //if selectedproject is null, set it          
          if (!selectedProject) {
            setSelectedProject(response.projects[0]);
          }
        } else if (dashboardData && dashboardData.data && Array.isArray(dashboardData.data)) {
          setProjects(dashboardData.data);
          
          // Set the first project as selected by default
          if (dashboardData.data.length > 0 && !selectedProjectId) {
            setSelectedProjectId(dashboardData.data[0].projectId);
            //set seleted project
            setSelectedProject(dashboardData.data[0]);
          }
        }
      } catch (err) {
        setError('Failed to load dashboard data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [selectedProjectId]);

  // Filter data based on selected project
  useEffect(() => {
    if (data && selectedProjectId) {
      // Always find and set the selected project based on selectedProjectId
      const sProject = projects.find(p => p.projectId === selectedProjectId);
      
      if (sProject) {
        setSelectedProject(sProject);
        
        if (sProject.recentIterations) {
          // Sort iterations by date (most recent first)
          const sortedProject = {
            ...sProject,
            recentIterations: [...sProject.recentIterations].sort((a, b) => {
              return new Date(b.EndDate).getTime() - new Date(a.EndDate).getTime();
            })
          };
          
          // Set the selected iteration to the most recent one by default
          if (sortedProject.recentIterations.length > 0) {
            setSelectedIterationIndex(0); // Most recent iteration is at index 0
          }
          
          setDisplayData(sortedProject);
        }
      } else if (projects.length > 0) {
        // Fallback to first project if selected project not found
        const fallbackProject = projects[0];
        setSelectedProjectId(fallbackProject.projectId);
        setSelectedProject(fallbackProject);
      }
    }
  }, [data, selectedProjectId, projects, setSelectedProject]);

  // Handle opening modal for AI summary
  const handleOpenModal = (title: string, content: string, type?: 'iteration', iterationId?: string) => {
    setModalContent({
      isOpen: true,
      title,
      content,
      type,
      iterationId
    });
  };

  // Handle closing modal
  const handleCloseModal = () => {
    setModalContent({
      isOpen: false,
      title: '',
      content: '',
      type: undefined,
      iterationId: undefined
    });
  };

  // Handle generating AI summary for an iteration
  const handleGenerateIterationSummary = async (iterationId: string, projectId: string) => {
    if (!projectId || !iterationId) return;

    // Interrupt any ongoing stream for this iteration
    const existingStream = currentStreams[iterationId];
    if (existingStream) {
      await existingStream.cancel();
      setCurrentStreams(prev => ({ ...prev, [iterationId]: null }));
    }

    try {
      setGeneratingAI(prev => ({ ...prev, [iterationId]: true }));
      
      // Clear existing content
      setIterationAISummaries(prev => ({ ...prev, [iterationId]: '' }));

      const stream = await generateAiSummary('iteration', iterationId, projectId);
      setCurrentStreams(prev => ({ ...prev, [iterationId]: stream.stream }));

      for await (const chunk of stream) {
        setIterationAISummaries(prev => ({ 
          ...prev, 
          [iterationId]: (prev[iterationId] || '') + chunk
        }));
      }
    } catch (error) {
      console.error('Error generating iteration AI summary:', error);
      const errorMessage = 'Error generating AI summary. Please try again.';
      setIterationAISummaries(prev => ({ 
        ...prev, 
        [iterationId]: errorMessage 
      }));
    } finally {
      setGeneratingAI(prev => ({ ...prev, [iterationId]: false }));
      setCurrentStreams(prev => ({ ...prev, [iterationId]: null }));
    }
  };


  
  // Handle project selection
  const handleSelectProject = (projectId: number) => {
    setSelectedProjectId(projectId);
    //set seleted project
    setSelectedProject(projects.find(p => p.projectId === projectId));
  };
  

  // Parse top over-estimate items from JSON string
  const parseTopOverEstimateItems = (itemsStr: string | undefined): TopOverEstimateItem[] => {
    if (!itemsStr) return [];
    
    try {
      return JSON.parse(itemsStr);
    } catch (error) {
      console.error('Error parsing top over-estimate items:', error);
      return [];
    }
  };

  const canGenerateAiSummary = (aiGeneratedDate: string | undefined): boolean => {
    return aiGeneratedDate && selectedProject?.summaryDate ? new Date(aiGeneratedDate) < new Date(selectedProject.summaryDate) : true;
  };

  // If loading, show loading indicator
  if (loading) {
    return <LoadingSpinner title="Loading iterations data..." />;
  }

  // If error, show error message
  if (error) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '1.5rem', color: '#f44336' }}>{error}</div>
      </div>
    );
  }

  // Get the selected iteration
  const selectedIteration = displayData?.recentIterations?.[selectedIterationIndex];

  //all ai summaries from iterations as one string
  //const aiSummaries = displayData?.recentIterations?.map((iteration: IterationData) => iteration.AISummary).join('\n');

  let dataSnapshotRange = ``;
  if(selectedProject)
  {
    dataSnapshotRange = `${format(new Date(selectedProject.snapshotStartDate), 'do MMMM yyyy HH:mm')} 
    - ${format(new Date(selectedProject.summaryDate), 'do MMMM yyyy HH:mm')}`;
  }

 
  // Parse top over-estimate items
  const topOverEstimateItems = selectedIteration ? 
    parseTopOverEstimateItems(selectedIteration.TopOverEstimateItems) : [];

  return (
    <div style={{ 
      padding: isMobile ? '1rem' : '0 1rem',
      paddingLeft: isMobile ? 'clamp(1rem, 4vw, 2rem)' : '1rem',
      paddingRight: isMobile ? 'clamp(1rem, 4vw, 2rem)' : '1rem',
      paddingBottom: '2rem',
      paddingTop: '1rem',
      width: '100%',
      maxWidth: '100vw',
      overflowX: 'hidden',
      boxSizing: 'border-box',
      minWidth: 0
    }}>
      {/* Mobile fixed top project selector */}
      {isMobile && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 999,
            paddingTop: 40,
            background: '#ffffff',
            borderBottom: '1px solid #e5e7eb',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }}
        >
          <div style={{ maxWidth: 1400, margin: '0 auto', padding: '12px 16px' }}>
            <div style={{ 
              fontSize: 20, 
              fontWeight: 300, 
              color: '#374151', 
              marginBottom: 8, 
              textAlign: 'center',
              fontFamily: 'Raleway, sans-serif'
            }}>
              Iterations Overview
            </div>
            {projects && projects.length > 0 && (
              <select
                value={selectedProjectId || ''}
                onChange={(e) => handleSelectProject(Number(e.target.value))}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid #d1d5db',
                  background: '#f9fafb',
                  fontSize: 14,
                  color: '#111827'
                }}
              >
                {projects.map((project) => (
                  <option key={project.projectId} value={project.projectId}>
                    {project.projectName}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      )}
      
      {/* Spacer for mobile fixed header */}
      {isMobile && <div style={{ height: '50px' }} />}
      {/* Desktop header - hidden on mobile */}
      {!isMobile && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          flexDirection: 'row',
          gap: '1rem'
        }}>
          <h1 style={{ 
            margin: 0, 
            fontSize: 'clamp(1.5rem, 4vw, 2rem)',
            lineHeight: '1.2',
            wordBreak: 'break-word',
            maxWidth: '100%',
            overflow: 'hidden',
            fontFamily: 'Raleway, sans-serif',
            fontWeight: 300,
          }}>{`Development Iterations (${selectedProject?.projectName})`}</h1>
          
          <ProjectSelector 
            projects={projects} 
            selectedProjectId={selectedProjectId} 
            onSelectProject={handleSelectProject} 
            windowWidth={windowWidth}
          />
        </div>
      )}
      <div style={{ marginBottom: '2rem' }}>
      {selectedProject?.summaryDate && (
        <p className="dashboard-subtitle" style={{ marginBottom: '2rem', color: '#666', fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
          Data snapshot: {dataSnapshotRange}
        </p>
      )}
      </div>

      {displayData && selectedIteration ? (
        <>
          {/* Iteration Overview */}
          <div style={{ 
            background: 'linear-gradient(115deg, rgb(34, 34, 69) 25%, rgb(77, 182, 159) 162%)',
            color: '#fff',
            borderRadius: 16,
            padding: 'clamp(1rem, 3vw, 1.5rem)',
            marginBottom: '2rem',
            boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
            maxWidth: '100%',
            overflowX: 'hidden',
            boxSizing: 'border-box'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: windowWidth >= 768 ? 'center' : 'flex-start',
              flexDirection: windowWidth >= 768 ? 'row' : 'column',
              gap: windowWidth >= 768 ? '0' : '0.5rem',
              marginBottom: '1rem',
              maxWidth: '100%',
              overflow: 'hidden'
            }}>
              <div>
                <h2 style={{ 
                  margin: 0, 
                  fontSize: windowWidth >= 768 ? '1.75rem' : '1.5rem',
                  maxWidth: '100%',
                  overflow: 'hidden'
                }}>Iteration Trends</h2>                
              </div>
              <div style={{ 
                background: 'rgba(255,255,255,0.2)',
                padding: '0.5rem 1rem',
                borderRadius: 20,
                fontWeight: 'bold',
                fontSize: windowWidth >= 768 ? '1rem' : '0.875rem',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%'
              }}>
                Latest Iteration: {displayData.recentIterations[0].IterationName}
              </div>
            </div>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: windowWidth >= 1024 ? 'repeat(4, 1fr)' : 
                                 windowWidth >= 768 ? 'repeat(2, 1fr)' : 
                                 'repeat(2, 1fr)', 
              gap: 'clamp(0.75rem, 2vw, 1rem)',
              marginTop: 'clamp(0.75rem, 2vw, 1rem)'
            }}>
              <div style={{ 
                background: 'rgba(255,255,255,0.1)', 
                borderRadius: 8, 
                padding: '1rem',
                minWidth: 0,
                overflow: 'hidden',
                boxSizing: 'border-box'
              }}>
                <div style={{ fontSize: '0.875rem', opacity: 0.8 }}>Story Points Completion</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: '0.5rem 0' }}>
                  {Math.round((displayData.recentIterations[0].StoryPointsCompleted / displayData.recentIterations[0].StoryPointsPlanned) * 100)}%
                </div>
                {displayData.recentIterations.length > 1 && (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    fontSize: '0.875rem',
                    color: displayData.recentIterations[0].StoryPointsCompleted / displayData.recentIterations[0].StoryPointsPlanned > 
                           displayData.recentIterations[1].StoryPointsCompleted / displayData.recentIterations[1].StoryPointsPlanned ? 
                           '#4caf50' : '#f44336'
                  }}>
                    {displayData.recentIterations[0].StoryPointsCompleted / displayData.recentIterations[0].StoryPointsPlanned > 
                     displayData.recentIterations[1].StoryPointsCompleted / displayData.recentIterations[1].StoryPointsPlanned ? '↑' : '↓'}
                    <span style={{ marginLeft: '0.25rem', fontSize: windowWidth >= 768 ? '0.875rem' : '0.75rem' }}>
                      {Math.abs(Math.round(((displayData.recentIterations[0].StoryPointsCompleted / displayData.recentIterations[0].StoryPointsPlanned) - 
                      (displayData.recentIterations[1].StoryPointsCompleted / displayData.recentIterations[1].StoryPointsPlanned)) * 100))}% from previous
                    </span>
                  </div>
                )}
              </div>
              
              <div style={{ 
                background: 'rgba(255,255,255,0.1)', 
                borderRadius: 8, 
                padding: '1rem',
                minWidth: 0,
                overflow: 'hidden',
                boxSizing: 'border-box'
              }}>
                <div style={{ fontSize: '0.875rem', opacity: 0.8 }}>Work Item Completion</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: '0.5rem 0' }}>
                  {Math.round(displayData.recentIterations[0].CompletionPercentage)}%
                </div>
                {displayData.recentIterations.length > 1 && (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    fontSize: '0.875rem',
                    color: displayData.recentIterations[0].CompletionPercentage > displayData.recentIterations[1].CompletionPercentage ? 
                           '#4caf50' : '#f44336'
                  }}>
                    {displayData.recentIterations[0].CompletionPercentage > displayData.recentIterations[1].CompletionPercentage ? '↑' : '↓'}
                    <span style={{ marginLeft: '0.25rem' }}>
                      {Math.abs(Math.round(displayData.recentIterations[0].CompletionPercentage - displayData.recentIterations[1].CompletionPercentage))}% from previous
                    </span>
                  </div>
                )}
              </div>
              
              <div style={{ 
                background: 'rgba(255,255,255,0.1)', 
                borderRadius: 8, 
                padding: '1rem',
                minWidth: 0,
                overflow: 'hidden',
                boxSizing: 'border-box'
              }}>
                <div style={{ fontSize: '0.875rem', opacity: 0.8 }}>Bug Resolution Rate</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: '0.5rem 0' }}>
                  {displayData.recentIterations[0].BugsCreated > 0 ? 
                    Math.round((displayData.recentIterations[0].BugsResolved / displayData.recentIterations[0].BugsCreated) * 100) : 100}%
                </div>
                {displayData.recentIterations.length > 1 && displayData.recentIterations[1].BugsCreated > 0 && (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    fontSize: '0.875rem',
                    color: (displayData.recentIterations[0].BugsResolved / displayData.recentIterations[0].BugsCreated) > 
                           (displayData.recentIterations[1].BugsResolved / displayData.recentIterations[1].BugsCreated) ? 
                           '#4caf50' : '#f44336'
                  }}>
                    {(displayData.recentIterations[0].BugsResolved / displayData.recentIterations[0].BugsCreated) > 
                     (displayData.recentIterations[1].BugsResolved / displayData.recentIterations[1].BugsCreated) ? '↑' : '↓'}
                    <span style={{ marginLeft: '0.25rem' }}>
                      {Math.abs(Math.round(((displayData.recentIterations[0].BugsResolved / displayData.recentIterations[0].BugsCreated) - 
                      (displayData.recentIterations[1].BugsResolved / displayData.recentIterations[1].BugsCreated)) * 100))}% from previous
                    </span>
                  </div>
                )}
              </div>
              
              <div style={{ 
                background: 'rgba(255,255,255,0.1)', 
                borderRadius: 8, 
                padding: '1rem',
                minWidth: 0,
                overflow: 'hidden',
                boxSizing: 'border-box'
              }}>
                <div style={{ fontSize: '0.875rem', opacity: 0.8 }}>Build Success Rate</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: '0.5rem 0' }}>
                  {Math.round((1 - displayData.recentIterations[0].BuildFailureRate))}%
                </div>
                {displayData.recentIterations.length > 1 && (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    fontSize: '0.875rem',
                    color: (1 - displayData.recentIterations[0].BuildFailureRate) > 
                           (1 - displayData.recentIterations[1].BuildFailureRate) ? 
                           '#4caf50' : '#f44336'
                  }}>
                    {(1 - displayData.recentIterations[0].BuildFailureRate) > 
                     (1 - displayData.recentIterations[1].BuildFailureRate) ? '↑' : '↓'}
                    <span style={{ marginLeft: '0.25rem' }}>
                      {Math.abs(Math.round(((1 - displayData.recentIterations[0].BuildFailureRate) - 
                      (1 - displayData.recentIterations[1].BuildFailureRate))))}% from previous
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* AI Summaries of Latest Iterations */}
          <div style={{ 
            background: '#fff', 
            borderRadius: 12, 
            padding: 'clamp(1rem, 3vw, 1.5rem)', 
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            marginBottom: '2rem',
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
            overflowX: 'hidden'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Executive Insights of Latest Iterations</h3>
            
            {displayData.recentIterations.length > 0 ? (
              windowWidth < 768 ? (
                <MobileGlassSlider
                  height={200}
                  items={displayData.recentIterations.map((iteration: IterationData) => {
                    const currentSummary = iterationAISummaries[iteration.IterationId] || iteration.AISummary;
                    const isGenerating = generatingAI[iteration.IterationId];
                    
                    return (
                      <div key={iteration.IterationId}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: '12px'
                        }}>
                          <div>
                            <h4 style={{
                              fontSize: '14px',
                              color: '#3949ab',
                              margin: '0 0 4px 0'
                            }}>
                              {iteration.IterationName}
                            </h4>
                            <div style={{
                              fontSize: '12px',
                              color: '#666',
                              fontWeight: 'normal'
                            }}>
                              {format(new Date(iteration.StartDate), 'do MMMM yyyy')} - {format(new Date(iteration.EndDate), 'do MMMM yyyy')}
                            </div>
                          </div>
                          
                          <IconButton
                            onClick={() => handleGenerateIterationSummary(iteration.IterationId, selectedProjectId?.toString() || '')}
                            disabled={isGenerating || !selectedProjectId}
                            title={currentSummary ? "Regenerate AI Analysis" : "Generate AI Analysis"}
                            icon="analytics"
                            isLoading={isGenerating}
                            size="medium"
                            variant="default"
                          />
                        </div>
                        
                        <div 
                          style={{
                            fontSize: '13px',
                            color: '#333',
                            lineHeight: '1.4',
                            height: '120px',
                            overflow: 'hidden',
                            cursor: 'pointer'
                          }}
                          onClick={() => {
                            handleOpenModal(
                              `AI Analysis - ${iteration.IterationName}`,
                              currentSummary || 'Click the analysis button to generate exec insights for this iteration.',
                              'iteration',
                              iteration.IterationId
                            );
                          }}
                          title="Click to view full content"
                        >
                          <div 
                            style={{
                              display: '-webkit-box',
                              WebkitLineClamp: 7,
                              WebkitBoxOrient: 'vertical' as any,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                            dangerouslySetInnerHTML={{ 
                              __html: formatMarkdown(currentSummary ? `***click to view full summary***\n\n${currentSummary}` : 'Click the analysis button to generate exec insights for this iteration.')
                            }} 
                          />
                        </div>
                      </div>
                    );
                  })}
                />
              ) : (
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
                  gap: '16px',
                  width: '100%',
                  maxWidth: '100%'
                }}>
                  {displayData.recentIterations.map((iteration: IterationData, index: number) => {
                    const currentSummary = iterationAISummaries[iteration.IterationId] || iteration.AISummary;
                    const isGenerating = generatingAI[iteration.IterationId];
                    
                    return (
                      <div key={iteration.IterationId} style={{
                        background: '#f8f9fa',
                        borderRadius: '8px',
                        padding: 'clamp(12px, 3vw, 16px)',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                        width: '100%',
                        minWidth: 0,
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        overflow: 'hidden'
                      }}>
                        <div style={{ 
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: '12px'
                        }}>
                          <div>
                            <h4 style={{ 
                              fontSize: '14px',
                              color: '#3949ab',
                              margin: '0 0 4px 0'
                            }}>
                              {iteration.IterationName}
                            </h4>
                            <div style={{ 
                              fontSize: '12px',
                              color: '#666',
                              fontWeight: 'normal'
                            }}>
                              {format(new Date(iteration.StartDate), 'do MMMM yyyy')} - {format(new Date(iteration.EndDate), 'do MMMM yyyy')}
                            </div>
                          </div>
                          
                          <IconButton
                            onClick={() => handleGenerateIterationSummary(iteration.IterationId, selectedProjectId?.toString() || '')}
                            disabled={isGenerating || !selectedProjectId}
                            title={currentSummary ? "Regenerate AI Analysis" : "Generate AI Analysis"}
                            icon="analytics"
                            isLoading={isGenerating}
                            size="medium"
                            variant="default"
                          />
                        </div>
                        
                        <div style={{ 
                          fontSize: currentSummary ? '6px' : '14px',
                          color: '#333',
                          lineHeight: '1.4',
                          height: '80px',
                          overflow: 'hidden',
                          position: 'relative'
                        }}>
                          <div
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              cursor: 'pointer',
                              zIndex: 1
                            }}
                            onClick={() => {
                              handleOpenModal(
                                `AI Analysis - ${iteration.IterationName}`,
                                currentSummary || 'Click the analysis button to generate exec insights for this iteration.',
                                'iteration',
                                iteration.IterationId
                              );
                            }}
                            title="Click to view full content"
                          />
                          
                          <div 
                            style={{
                              display: '-webkit-box',
                              WebkitLineClamp: currentSummary ? 10 : 4,
                              WebkitBoxOrient: 'vertical' as any,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              position: 'relative',
                              zIndex: 0
                            }}
                            dangerouslySetInnerHTML={{ 
                              __html: formatMarkdown(currentSummary || 'Click the analysis button to generate exec insights for this iteration.') 
                            }} 
                          />
                          {currentSummary && (
                            <div style={{
                              position: 'absolute',
                              bottom: '0',
                              right: '0',
                              background: 'linear-gradient(to right, transparent, rgba(248, 249, 250, 1) 50%)',
                              padding: '0 4px',
                              fontSize: '6px',
                              color: '#666',
                              pointerEvents: 'none',
                              zIndex: 0
                            }}>
                              ...
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            ) : (
              <div style={{ 
                background: 'rgba(158, 158, 158, 0.1)', 
                borderRadius: 8, 
                padding: '1rem',
                borderLeft: '4px solid #9e9e9e'
              }}>
                <p style={{ margin: 0, fontSize: '0.9rem' }}>
                  No iteration data available to display.
                </p>
              </div>
            )}
          </div>



          {/* Trend Charts */}
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: windowWidth >= 1024 ? '1fr 1fr' : '1fr',
            gap: '1.5rem',
            marginBottom: '2rem',
            width: '100%',
            maxWidth: '100%',
            overflowX: 'hidden'
          }}>

          {/* Estimation Accuracy Trend */}
          <div style={{ 
            background: '#fff', 
            borderRadius: 12, 
            padding: 'clamp(1rem, 3vw, 1.5rem)', 
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            marginBottom: '2rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
              {displayData.recentIterations.length >= 2 && (
                <div style={{ 
                  padding: '0.25rem 0.5rem', 
                  borderRadius: '4px',
                  backgroundColor: (() => {
                    const latest = displayData.recentIterations[0];
                    const previous = displayData.recentIterations[1];
                    if (!latest || !previous || latest.PlannedWorkItemsCount === 0 || previous.PlannedWorkItemsCount === 0) return 'rgba(158, 158, 158, 0.2)';
                    
                    const latestInaccuracy = ((latest.ItemsOverEstimate || 0) + (latest.ItemsUnderEstimate || 0)) / latest.PlannedWorkItemsCount;
                    const previousInaccuracy = ((previous.ItemsOverEstimate || 0) + (previous.ItemsUnderEstimate || 0)) / previous.PlannedWorkItemsCount;
                    
                    return latestInaccuracy < previousInaccuracy ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)';
                  })(),
                  color: (() => {
                    const latest = displayData.recentIterations[0];
                    const previous = displayData.recentIterations[1];
                    if (!latest || !previous || latest.PlannedWorkItemsCount === 0 || previous.PlannedWorkItemsCount === 0) return '#9e9e9e';
                    
                    const latestInaccuracy = ((latest.ItemsOverEstimate || 0) + (latest.ItemsUnderEstimate || 0)) / latest.PlannedWorkItemsCount;
                    const previousInaccuracy = ((previous.ItemsOverEstimate || 0) + (previous.ItemsUnderEstimate || 0)) / previous.PlannedWorkItemsCount;
                    
                    return latestInaccuracy < previousInaccuracy ? '#4caf50' : '#f44336';
                  })(),
                  fontWeight: 'bold',
                  fontSize: '1.1rem'
                }}>
                  {(() => {
                    const latest = displayData.recentIterations[0];
                    const previous = displayData.recentIterations[1];
                    if (!latest || !previous || latest.PlannedWorkItemsCount === 0 || previous.PlannedWorkItemsCount === 0) return '—';
                    
                    const latestInaccuracy = ((latest.ItemsOverEstimate || 0) + (latest.ItemsUnderEstimate || 0)) / latest.PlannedWorkItemsCount;
                    const previousInaccuracy = ((previous.ItemsOverEstimate || 0) + (previous.ItemsUnderEstimate || 0)) / previous.PlannedWorkItemsCount;
                    
                    return latestInaccuracy < previousInaccuracy ? '😊' : '😞';
                  })()}
                </div>
              )}
              <h3 style={{ margin: 0, marginLeft: '8px' }}>Estimation Accuracy Trend</h3>
            </div>
            <div style={{ height: '300px' }}>
              <Line
                data={{
                  labels: displayData.recentIterations
                    .slice()
                    .reverse()
                    .map((iteration: IterationData) => iteration.IterationName),
                  datasets: [
                    {
                      label: 'Overrunning Items (%)',
                      data: displayData.recentIterations
                        .slice()
                        .reverse()
                        .map((iteration: IterationData) => {
                          // Get the total assigned tasks from developers for this iteration
                          const totalAssignedTasks = displayData.Developers && Array.isArray(displayData.Developers)
                            ? displayData.Developers.reduce((sum: number, dev: any) => {
                                if (dev.IterationData && Array.isArray(dev.IterationData)) {
                                  const iterData = dev.IterationData.find((iter: any) => 
                                    iter.IterationId === iteration.IterationId
                                  );
                                  return sum + (iterData?.WorkItemTasksAssigned || 0);
                                }
                                return sum;
                              }, 0)
                            : iteration.PlannedWorkItemsCount; // Fallback to old method if no developer data
                          
                          // Use the aggregated WorkItemTasksAssigned as denominator if available
                          const denominator = totalAssignedTasks > 0 ? totalAssignedTasks : iteration.PlannedWorkItemsCount;
                          
                          return denominator > 0 && iteration.ItemsOverEstimate !== undefined
                            ? (iteration.ItemsOverEstimate / denominator) * 100
                            : 0;
                        }),
                      borderColor: '#ff5252',
                      backgroundColor: 'rgba(255, 82, 82, 0.79)',
                      fill: false,
                      tension: 0.4
                    },
                    {
                      label: 'Early Delivered Items (%)',
                      data: displayData.recentIterations
                        .slice()
                        .reverse()
                        .map((iteration: IterationData) => {
                          // Get the total assigned tasks from developers for this iteration
                          const totalAssignedTasks = displayData.Developers && Array.isArray(displayData.Developers)
                            ? displayData.Developers.reduce((sum: number, dev: any) => {
                                if (dev.IterationData && Array.isArray(dev.IterationData)) {
                                  const iterData = dev.IterationData.find((iter: any) => 
                                    iter.IterationId === iteration.IterationId
                                  );
                                  return sum + (iterData?.WorkItemTasksAssigned || 0);
                                }
                                return sum;
                              }, 0)
                            : iteration.PlannedWorkItemsCount; // Fallback to old method if no developer data
                          
                          // Use the aggregated WorkItemTasksAssigned as denominator if available
                          const denominator = totalAssignedTasks > 0 ? totalAssignedTasks : iteration.PlannedWorkItemsCount;
                          
                          return denominator > 0 && iteration.ItemsUnderEstimate !== undefined
                            ? (iteration.ItemsUnderEstimate / denominator) * 100
                            : 0;
                        }),
                      borderColor: '#3e8e41',
                      backgroundColor: 'rgba(50, 136, 50, 0.67)',
                      fill: false,
                      tension: 0.4
                    }
                  ]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    y: {
                      stacked: false,
                      beginAtZero: true,
                      max: 100,
                      title: {
                        display: true,
                        text: 'Percentage of Work Items',
                        font: { weight: 'bold' }
                      },
                      ticks: {
                        callback: (value) => `${value}%`
                      }
                    },
                    x: {
                      title: {
                        display: true,
                        text: 'Iterations',
                        font: { weight: 'bold' }
                      }
                    }
                  },
                  plugins: {
                    tooltip: {
                      callbacks: {
                        label: (context) => `${context.dataset.label}: ${context.parsed.y.toFixed(1)}%`
                      }
                    },
                    legend: {
                      position: 'top',
                    },
                    title: {
                      display: true,
                      text: 'Estimation Accuracy Over Time'
                    }
                  }
                }}
              />
            </div>
          </div>         


          {/* PR Review Time Trend */}
          <div style={{ 
            background: '#fff', 
            borderRadius: 12, 
            padding: 'clamp(1rem, 3vw, 1.5rem)', 
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            marginBottom: '2rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
              {displayData.recentIterations.length >= 2 && (
                <div style={{ 
                  padding: '0.25rem 0.5rem', 
                  borderRadius: '4px',
                  backgroundColor: (() => {
                    const latest = displayData.recentIterations[0];
                    const previous = displayData.recentIterations[1];
                    if (!latest || !previous) return 'rgba(158, 158, 158, 0.2)';
                    
                    return latest.AveragePRReviewTime < previous.AveragePRReviewTime ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)';
                  })(),
                  color: (() => {
                    const latest = displayData.recentIterations[0];
                    const previous = displayData.recentIterations[1];
                    if (!latest || !previous) return '#9e9e9e';
                    
                    return latest.AveragePRReviewTime < previous.AveragePRReviewTime ? '#4caf50' : '#f44336';
                  })(),
                  fontWeight: 'bold',
                  fontSize: '1.1rem'
                }}>
                  {(() => {
                    const latest = displayData.recentIterations[0];
                    const previous = displayData.recentIterations[1];
                    if (!latest || !previous) return '—';
                    
                    return latest.AveragePRReviewTime < previous.AveragePRReviewTime ? '😊' : '😞';
                  })()}
                </div>
              )}
              <h3 style={{ margin: 0, marginLeft: '8px' }}>PR Review Time Trend</h3>
            </div>
            <div style={{ height: '300px' }}>
              <Line
                data={{
                  labels: displayData.recentIterations
                    .slice()
                    .reverse()
                    .map((iteration: IterationData) => iteration.IterationName),
                  datasets: [
                    {
                      label: 'Average PR Review Time (hours)',
                      data: displayData.recentIterations
                        .slice()
                        .reverse()
                        .map((iteration: IterationData) => iteration.AveragePRReviewTime || 0),
                      borderColor: '#2196f3',
                      backgroundColor: 'rgba(33, 150, 243, 0.2)',
                      fill: false,
                      tension: 0.4,
                      pointBackgroundColor: '#2196f3',
                      pointRadius: 4,
                      pointHoverRadius: 6
                    }
                  ]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    y: {
                      beginAtZero: true,
                      title: {
                        display: true,
                        text: 'Hours',
                        font: { weight: 'bold' }
                      },
                      ticks: {
                        callback: (value) => `${value}h`
                      }
                    },
                    x: {
                      title: {
                        display: true,
                        text: 'Iterations',
                        font: { weight: 'bold' }
                      }
                    }
                  },
                  plugins: {
                    tooltip: {
                      callbacks: {
                        label: (context) => `Review Time: ${context.parsed.y.toFixed(1)} hours`
                      }
                    },
                    legend: {
                      position: 'top',
                    },
                    title: {
                      display: true,
                      text: 'PR Review Time Across Iterations'
                    }
                  }
                }}
              />
            </div>
            
          </div>

            {/* Completion Rate Trend */}
            <div style={{ 
              background: '#fff', 
              borderRadius: 12, 
              padding: '1.5rem', 
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
            }}>
              
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                {displayData.recentIterations.length >= 2 && (
                  <div style={{ 
                    padding: '0.25rem 0.5rem', 
                    borderRadius: '4px',
                    backgroundColor: (() => {
                      const latest = displayData.recentIterations[0];
                      const previous = displayData.recentIterations[1];
                      if (!latest || !previous) return 'rgba(158, 158, 158, 0.2)';
                      
                      return latest.CompletionPercentage > previous.CompletionPercentage ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)';
                    })(),
                    color: (() => {
                      const latest = displayData.recentIterations[0];
                      const previous = displayData.recentIterations[1];
                      if (!latest || !previous) return '#9e9e9e';
                      
                      return latest.CompletionPercentage > previous.CompletionPercentage ? '#4caf50' : '#f44336';
                    })(),
                    fontWeight: 'bold',
                    fontSize: '1.1rem'
                  }}>
                    {(() => {
                      const latest = displayData.recentIterations[0];
                      const previous = displayData.recentIterations[1];
                      if (!latest || !previous) return '—';
                      
                      return latest.CompletionPercentage > previous.CompletionPercentage ? '😊' : '😞';
                    })()}
                  </div>
                )}
                <h3 style={{ margin: 0, marginLeft: '8px' }}>Completion Rate Trend</h3>
              </div>
              <div style={{ height: 300 }}>
                <Bar
                  data={{
                    labels: displayData.recentIterations.map((i: IterationData) => i.IterationName).reverse(),
                    datasets: [
                      {
                        label: 'Completion %',
                        data: displayData.recentIterations.map((i: IterationData) => i.CompletionPercentage).reverse(),
                        backgroundColor: displayData.recentIterations.map((i: IterationData) => 
                          i.CompletionPercentage >= 90 ? 'rgba(76, 175, 80, 0.7)' : 
                          i.CompletionPercentage >= 75 ? 'rgba(255, 152, 0, 0.7)' : 
                          'rgba(244, 67, 54, 0.7)'
                        ).reverse(),
                        borderColor: displayData.recentIterations.map((i: IterationData) => 
                          i.CompletionPercentage >= 90 ? 'rgb(76, 175, 80)' : 
                          i.CompletionPercentage >= 75 ? 'rgb(255, 152, 0)' : 
                          'rgb(244, 67, 54)'
                        ).reverse(),
                        borderWidth: 1
                      }
                    ]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      y: {
                        beginAtZero: true,
                        max: 100,
                        title: {
                          display: true,
                          text: 'Completion %',
                          font: { weight: 'bold' }
                        },
                        ticks: {
                          callback: (value) => `${value}%`
                        }
                      },
                      x: {
                        title: {
                          display: true,
                          text: 'Iterations',
                          font: { weight: 'bold' }
                        }
                      }
                    },
                    plugins: {
                      tooltip: {
                        callbacks: {
                          label: (context) => `Completion: ${context.parsed.y.toFixed(1)}%`
                        }
                      }
                    }
                  }}
                />
              </div>
              
            </div>

            {/* Story Points Trend */}
            <div style={{ 
              background: '#fff', 
              borderRadius: 12, 
              padding: '1.5rem', 
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                {displayData.recentIterations.length >= 2 && (
                  <div style={{ 
                    padding: '0.25rem 0.5rem', 
                    borderRadius: '4px',
                    backgroundColor: (() => {
                      const latest = displayData.recentIterations[0];
                      const previous = displayData.recentIterations[1];
                      if (!latest || !previous) return 'rgba(158, 158, 158, 0.2)';
                      
                      const latestRatio = latest.StoryPointsCompleted / latest.StoryPointsPlanned;
                      const previousRatio = previous.StoryPointsCompleted / previous.StoryPointsPlanned;
                      
                      return latestRatio > previousRatio ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)';
                    })(),
                    color: (() => {
                      const latest = displayData.recentIterations[0];
                      const previous = displayData.recentIterations[1];
                      if (!latest || !previous) return '#9e9e9e';
                      
                      const latestRatio = latest.StoryPointsCompleted / latest.StoryPointsPlanned;
                      const previousRatio = previous.StoryPointsCompleted / previous.StoryPointsPlanned;
                      
                      return latestRatio > previousRatio ? '#4caf50' : '#f44336';
                    })(),
                    fontWeight: 'bold',
                    fontSize: '1.1rem'
                  }}>
                    {(() => {
                      const latest = displayData.recentIterations[0];
                      const previous = displayData.recentIterations[1];
                      if (!latest || !previous) return '—';
                      
                      const latestCompleted = latest.StoryPointsCompleted;
                      const previousCompleted = previous.StoryPointsCompleted;
                      
                      return latestCompleted > previousCompleted ? '😊' : '😞';
                    })()}
                  </div>
                )}
                <h3 style={{ margin: 0, marginLeft: '8px' }}>Story Points Trend</h3>
              </div>
              <div style={{ height: 300 }}>
                <Bar
                  data={{
                    labels: displayData.recentIterations.map((i: IterationData) => i.IterationName).reverse(),
                    datasets: [
                      {
                        label: 'Planned',
                        data: displayData.recentIterations.map((i: IterationData) => i.StoryPointsPlanned).reverse(),
                        backgroundColor: 'rgba(54, 162, 235, 0.5)',
                        borderColor: 'rgb(54, 162, 235)',
                        borderWidth: 1
                      },
                      {
                        label: 'Completed',
                        data: displayData.recentIterations.map((i: IterationData) => i.StoryPointsCompleted).reverse(),
                        backgroundColor: 'rgba(75, 192, 192, 0.5)',
                        borderColor: 'rgb(75, 192, 192)',
                        borderWidth: 1
                      }
                    ]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      y: {
                        beginAtZero: true,
                        title: {
                          display: true,
                          text: 'Story Points',
                          font: { weight: 'bold' }
                        }
                      },
                      x: {
                        title: {
                          display: true,
                          text: 'Iterations',
                          font: { weight: 'bold' }
                        }
                      }
                    }
                  }}
                />
              </div>
            </div>

            {/* Bug Trend */}
            <div style={{ 
              background: '#fff', 
              borderRadius: 12, 
              padding: '1.5rem', 
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                {displayData.recentIterations.length >= 2 && (
                  <div style={{ 
                    padding: '0.25rem 0.5rem', 
                    borderRadius: '4px',
                    backgroundColor: (() => {
                      const latest = displayData.recentIterations[0];
                      const previous = displayData.recentIterations[1];
                      if (!latest || !previous) return 'rgba(158, 158, 158, 0.2)';
                      
                      // For bugs, we consider the trend positive if the resolution rate is improving
                      const latestResolutionRate = latest.BugsResolved / (latest.BugsCreated || 1);
                      const previousResolutionRate = previous.BugsResolved / (previous.BugsCreated || 1);
                      
                      return latestResolutionRate > previousResolutionRate ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)';
                    })(),
                    color: (() => {
                      const latest = displayData.recentIterations[0];
                      const previous = displayData.recentIterations[1];
                      if (!latest || !previous) return '#9e9e9e';
                      
                      const latestResolutionRate = latest.BugsResolved / (latest.BugsCreated || 1);
                      const previousResolutionRate = previous.BugsResolved / (previous.BugsCreated || 1);
                      
                      return latestResolutionRate > previousResolutionRate ? '#4caf50' : '#f44336';
                    })(),
                    fontWeight: 'bold',
                    fontSize: '1.1rem'
                  }}>
                    {(() => {
                      const latest = displayData.recentIterations[0];
                      const previous = displayData.recentIterations[1];
                      if (!latest || !previous) return '—';
                      
                      // const latestResolutionRate = latest.BugsResolved / (latest.BugsCreated || 1);
                      // const previousResolutionRate = previous.BugsResolved / (previous.BugsCreated || 1);
                      
                      // return latestResolutionRate > previousResolutionRate ? '😊' : '😞';
            
                      const latestBugsCreated = latest.BugsCreated;
                      const previousBugsCreated = previous.BugsCreated;
                      
                      return latestBugsCreated <= previousBugsCreated ? '😊' : '😞';
                    })()}
                  </div>
                )}
                <h3 style={{ margin: 0, marginLeft: '8px' }}>Bug Trend</h3>
              </div>
              <div style={{ height: 300 }}>
                <Bar
                  data={{
                    labels: displayData.recentIterations.map((i: IterationData) => i.IterationName).reverse(),
                    datasets: [
                      {
                        label: 'Created',
                        data: displayData.recentIterations.map((i: IterationData) => i.BugsCreated).reverse(),
                        backgroundColor: 'rgba(255, 99, 132, 0.5)',
                        borderColor: 'rgb(255, 99, 132)',
                        borderWidth: 1
                      },
                      {
                        label: 'Resolved',
                        data: displayData.recentIterations.map((i: IterationData) => i.BugsResolved).reverse(),
                        backgroundColor: 'rgba(75, 192, 192, 0.5)',
                        borderColor: 'rgb(75, 192, 192)',
                        borderWidth: 1
                      },
                      {
                        label: 'Active',
                        data: displayData.recentIterations.map((i: IterationData) => i.BugsActive).reverse(),
                        backgroundColor: 'rgba(255, 159, 64, 0.5)',
                        borderColor: 'rgb(255, 159, 64)',
                        borderWidth: 1
                      }
                    ]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      y: {
                        beginAtZero: true,
                        title: {
                          display: true,
                          text: 'Number of Bugs',
                          font: { weight: 'bold' }
                        }
                      },
                      x: {
                        title: {
                          display: true,
                          text: 'Iterations',
                          font: { weight: 'bold' }
                        }
                      }
                    }
                  }}
                />
              </div>
            </div>

            {/* Build Failure Rate Trend */}
            <div style={{ 
              background: '#fff', 
              borderRadius: 12, 
              padding: '1.5rem', 
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                {displayData.recentIterations.length >= 2 && (
                  <div style={{ 
                    padding: '0.25rem 0.5rem', 
                    borderRadius: '4px',
                    backgroundColor: (() => {
                      const latest = displayData.recentIterations[0];
                      const previous = displayData.recentIterations[1];
                      if (!latest || !previous) return 'rgba(158, 158, 158, 0.2)';
                      
                      return latest.BuildFailureRate < previous.BuildFailureRate ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)';
                    })(),
                    color: (() => {
                      const latest = displayData.recentIterations[0];
                      const previous = displayData.recentIterations[1];
                      if (!latest || !previous) return '#9e9e9e';
                      
                      return latest.BuildFailureRate < previous.BuildFailureRate ? '#4caf50' : '#f44336';
                    })(),
                    fontWeight: 'bold',
                    fontSize: '1.1rem'
                  }}>
                    {(() => {
                      const latest = displayData.recentIterations[0];
                      const previous = displayData.recentIterations[1];
                      if (!latest || !previous) return '—';
                      
                      return latest.BuildFailureRate < previous.BuildFailureRate ? '😊' : '😞';
                    })()}
                  </div>
                )}
                <h3 style={{ margin: 0, marginLeft: '8px' }}>Build Quality Trend</h3>
              </div>
              <div style={{ height: 300 }}>
                <Line
                  data={{
                    labels: displayData.recentIterations.map((i: IterationData) => i.IterationName).reverse(),
                    datasets: [
                      {
                        label: 'Build Success Rate',
                        data: displayData.recentIterations.map((i: IterationData) => (100-i.BuildFailureRate)).reverse(),
                        backgroundColor: 'rgba(75, 192, 192, 0.2)',
                        borderColor: 'rgb(75, 192, 192)',
                        borderWidth: 2,
                        tension: 0.3,
                        fill: true
                      }
                    ]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      y: {
                        beginAtZero: true,
                        max: 100,
                        title: {
                          display: true,
                          text: 'Success Rate %',
                          font: { weight: 'bold' }
                        },
                        ticks: {
                          callback: (value) => `${value}%`
                        }
                      },
                      x: {
                        title: {
                          display: true,
                          text: 'Iterations',
                          font: { weight: 'bold' }
                        }
                      }
                    },
                    plugins: {
                      tooltip: {
                        callbacks: {
                          label: (context) => `Success Rate: ${context.parsed.y.toFixed(1)}%`
                        }
                      }
                    }
                  }}
                />
              </div>
            </div>
          </div>          

          {/* Top Over-Estimate Items */}
          {topOverEstimateItems.length > 0 && (
            <div style={{ 
              background: '#fff', 
              borderRadius: 12, 
              padding: '1.5rem', 
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              marginBottom: 'clamp(2.75rem, 2vw, 1rem)'
            }}>
              <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Top Items Exceeding Estimates</h3>
              <div style={{ overflowX: 'auto', marginTop: '1rem' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #eee' }}>
                    <th style={{ textAlign: 'left', padding: '0.5rem', color: '#666' }}>Work Item</th>
                    <th style={{ textAlign: 'left', padding: '0.5rem', color: '#666' }}>Assignee</th>
                    <th style={{ textAlign: 'right', padding: '0.5rem', color: '#666' }}>Original (hours)</th>
                    <th style={{ textAlign: 'right', padding: '0.5rem', color: '#666' }}>Actual (hours)</th>
                    <th style={{ textAlign: 'right', padding: '0.5rem', color: '#666' }}>Ratio</th>
                  </tr>
                </thead>
                <tbody>
                  {topOverEstimateItems.map((item: TopOverEstimateItem) => (
                    <tr key={item.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                      <td style={{ padding: '0.75rem 0.5rem' }}>
                        <div style={{ fontWeight: 500 }}>({item.id}) - {item.title}</div>
                        <div style={{ fontSize: '0.8rem', color: '#666' }}>ID: {item.id}</div>
                      </td>
                      <td style={{ padding: '0.75rem 0.5rem' }}>{item.developerName}</td>
                      <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>{item.originalEstimate.toFixed(1)}</td>
                      <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>{item.actualDuration.toFixed(1)}</td>
                      <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right', color: '#f44336', fontWeight: 'bold' }}>
                        {item.overEstimateRatio.toFixed(1)}x
                      </td>
                    </tr>
                  ))}
                </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      ) : (
        <div style={{ 
          padding: 'clamp(1.5rem, 5vw, 3rem)', 
          textAlign: 'center', 
          background: '#f9f9f9', 
          borderRadius: 12,
          color: '#666'
        }}>
          <div style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>No iteration data available</div>
          <div>Select a project to view iteration details</div>
        </div>
      )}

      {/* AI Panel for insights and follow-up questions */}
      {/* Modal for full AI summary content */}
      {modalContent.isOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            width: windowWidth >= 768 ? '90%' : '95%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflowY: 'auto',
            padding: windowWidth >= 768 ? '0 24px 24px 24px' : '0 16px 16px 16px',
            margin: '0 8px'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '20px',
              borderBottom: '1px solid #e0e0e0',
              paddingBottom: '16px',
              paddingTop: '16px',
              position: 'sticky',
              top: 0,
              backgroundColor: 'white',
              zIndex: 1
            }}>
              <h2 style={{ 
                margin: 0, 
                fontSize: '20px', 
                fontWeight: 600,
                color: '#3949ab'
              }}>
                {modalContent.title}
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {/* Generate AI Summary Button in Modal */}
                {modalContent.type === 'iteration' && modalContent.iterationId && (
                  <IconButton
                    onClick={() => handleGenerateIterationSummary(modalContent.iterationId!, selectedProjectId?.toString() || '')}
                    disabled={generatingAI[modalContent.iterationId] || !selectedProjectId}
                    title={modalContent.content && !modalContent.content.includes('Click the analysis button') ? "Regenerate AI Analysis" : "Generate AI Analysis"}
                    icon={modalContent.content && !modalContent.content.includes('Click the analysis button') ? 'refresh' : 'analytics'}
                    isLoading={generatingAI[modalContent.iterationId]}
                    variant="primary"
                    size="medium"
                  />
                )}

                <button
                  onClick={handleCloseModal}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#666',
                    padding: '4px'
                  }}
                >
                  ×
                </button>
              </div>
            </div>
            
            <div style={{ 
              fontSize: '14px',
              lineHeight: '1.6',
              color: '#333'
            }}>
              <div dangerouslySetInnerHTML={{ 
                __html: formatMarkdown(
                  modalContent.type === 'iteration' && modalContent.iterationId
                    ? (iterationAISummaries[modalContent.iterationId] || modalContent.content)
                    : modalContent.content
                ) 
              }} />
              {modalContent.type === 'iteration' && modalContent.iterationId && generatingAI[modalContent.iterationId] && (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  color: '#666',
                  fontStyle: 'italic',
                  marginTop: '16px',
                  fontSize: '14px'
                }}>
                  <span className="material-icons" style={{ 
                    fontSize: '16px', 
                    marginRight: '8px',
                    animation: 'spin 1s linear infinite'
                  }}>
                    refresh
                  </span>
                  Generating AI analysis...
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add CSS for spin animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      
      <SlidingAiPanel 
        insight={selectedProject?.allIterationsAISummary?.summary}
        summaryCreatedOn={selectedProject?.allIterationsAISummary?.createdOn}
        canGenerateNewSummary={canGenerateAiSummary(selectedProject?.allIterationsAISummary?.createdOn)}
        contextType="all-iterations"
        entityName={"Project Insight"}
        entityId={selectedProjectId?.toString() || ''}
        projectId={selectedProjectId?.toString()}
        initialOpen={aiPanelOpen}
        buttonTitle={"Exec Delivery Insights"}
      />
    </div>
  );
};

export default DevelopmentIterations;
